/**
 * A minimal history router.
 *
 * The app has ten routes and no data loaders, so a router dependency would be
 * almost entirely unused weight. This is the whole contract: a subscribable
 * `location`, `navigate()`, a `<Link>` that behaves like an anchor for
 * middle-click and modifier-click, and pattern matching for `:params`.
 */

import { useCallback, useMemo, useSyncExternalStore } from 'react';
import type { AnchorHTMLAttributes, MouseEvent, ReactNode } from 'react';

const listeners = new Set<() => void>();
let snapshot = readLocation();

function readLocation(): string {
  return `${window.location.pathname}${window.location.search}`;
}

function emit(): void {
  snapshot = readLocation();
  for (const listener of listeners) listener();
}

window.addEventListener('popstate', emit);

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export interface NavigateOptions {
  replace?: boolean;
  /** Skip the scroll reset; used when a screen owns its own scroll position. */
  keepScroll?: boolean;
}

export function navigate(to: string, options: NavigateOptions = {}): void {
  const url = new URL(to, window.location.origin);
  const next = `${url.pathname}${url.search}`;
  if (options.replace) window.history.replaceState(null, '', next);
  else window.history.pushState(null, '', next);
  emit();
  if (!options.keepScroll) window.scrollTo(0, 0);
}

export interface RouteLocation {
  pathname: string;
  search: string;
  query: URLSearchParams;
}

export function useLocation(): RouteLocation {
  const href = useSyncExternalStore(subscribe, () => snapshot, () => snapshot);
  return useMemo(() => {
    const index = href.indexOf('?');
    const pathname = index === -1 ? href : href.slice(0, index);
    const search = index === -1 ? '' : href.slice(index);
    return { pathname, search, query: new URLSearchParams(search) };
  }, [href]);
}

export function useNavigate(): (to: string, options?: NavigateOptions) => void {
  return useCallback((to: string, options?: NavigateOptions) => navigate(to, options), []);
}

/**
 * Match `/movies/:accountId/:id` against a concrete pathname. Returns null on a
 * miss so a caller can fall through to the next pattern.
 */
export function matchPath(pattern: string, pathname: string): Record<string, string> | null {
  const expected = pattern.split('/').filter(Boolean);
  const actual = pathname.split('/').filter(Boolean);
  if (expected.length !== actual.length) return null;

  const params: Record<string, string> = {};
  for (let index = 0; index < expected.length; index += 1) {
    const segment = expected[index];
    const value = actual[index];
    if (segment.startsWith(':')) {
      params[segment.slice(1)] = decodeURIComponent(value);
      continue;
    }
    if (segment !== value) return null;
  }
  return params;
}

interface LinkProps extends Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> {
  to: string;
  replace?: boolean;
  children: ReactNode;
}

export function Link({ to, replace, onClick, children, ...rest }: LinkProps) {
  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event);
    // Leave the browser alone for anything that is not a plain left click, so
    // "open in new tab" keeps working.
    if (event.defaultPrevented) return;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate(to, replace ? { replace: true } : {});
  };

  return (
    <a href={to} onClick={handleClick} {...rest}>
      {children}
    </a>
  );
}
